import { useCallback } from 'react';
import type { DocumentMarker, Marker } from '@frameio/graphql-client/generated';
import { useGetMarkersFromSequenceLazyQuery } from '../../../../premiere/gql/queries/generated/GetMarkersFromSequence';
import { useGetDocumentMarkersLazyQuery } from '../../../gql';
import { usePluginFeatures } from '../../usePluginFeatures';

type SequenceMarkerInput = DocumentMarker | Marker;

function hostRowsToExportMarkers(
  markers: Array<SequenceMarkerInput | null | undefined>,
  startUnit: 'microseconds' | 'seconds'
): Marker[] {
  return markers
    .filter((m): m is SequenceMarkerInput => m != null)
    .map((m) => ({
      __typename: 'Marker' as const,
      comments: m.comments,
      deleted: m.deleted,
      duration: m.duration ?? undefined,
      frameCommentId: m.frameCommentId ?? '',
      id: m.id ?? undefined,
      name: m.name,
      start: startUnit === 'seconds' ? m.start * 1_000_000 : m.start,
    }));
}

/**
 * Fetch host markers, normalized to `Marker[]` with `start` in **microseconds**.
 *
 * - Agnostic (`canUseAgnosticCommentMarkerSync`): `documentMarkers.start` is already µs.
 * - Legacy: `getMarkersFromSequence.start` is **seconds** (the dva-js `* 1e6` change
 *   shipped with the agnostic bump, so pre-agnostic plugins still emit seconds); scale here.
 */
export function useDocumentMarkersMigrationQuery() {
  const { canUseAgnosticCommentMarkerSync } = usePluginFeatures();

  const [getDocumentMarkersQuery] = useGetDocumentMarkersLazyQuery({
    context: { resolveWithUXP: true },
    fetchPolicy: 'network-only',
  });

  const [getMarkersFromSequenceQuery] = useGetMarkersFromSequenceLazyQuery({
    context: { resolveWithUXP: true },
    fetchPolicy: 'network-only',
  });

  const getDocumentMarkers = useCallback(
    async ({
      sequenceId: documentId,
    }: {
      sequenceId: string;
    }): Promise<Marker[]> => {
      if (canUseAgnosticCommentMarkerSync) {
        const { data } = await getDocumentMarkersQuery({
          variables: { documentId },
        });

        return hostRowsToExportMarkers(
          data?.documentMarkers ?? [],
          'microseconds'
        );
      }

      const { data } = await getMarkersFromSequenceQuery({
        variables: { sequenceId: documentId },
      });

      return hostRowsToExportMarkers(
        data?.getMarkersFromSequence ?? [],
        'seconds'
      );
    },
    [
      canUseAgnosticCommentMarkerSync,
      getDocumentMarkersQuery,
      getMarkersFromSequenceQuery,
    ]
  );

  return {
    getDocumentMarkers,
  };
}
